import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { consumeFreshLogin } from "../auth/AuthProvider";
import { useSetLocale } from "../i18n/IntlRoot";
import { router } from "../router/router";
import { loadCapabilities } from "./bootstrap";
import { CapabilitiesProvider } from "./capabilities";
import type { SessionCapabilities } from "./types";

/**
 * Bootstraps the session: once authenticated, loads /v1/session/capabilities, applies the
 * locale, and renders the capabilities-scoped router. Loading + error are first-class states
 * (UX-003: a screen always states loading/error, never a blank).
 */
export function SessionGate() {
  const [caps, setCaps] = useState<SessionCapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setLocale = useSetLocale();

  useEffect(() => {
    let active = true;
    loadCapabilities()
      .then((c) => {
        if (!active) return;
        setCaps(c);
        setLocale(c.locale);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Could not load your session.");
      });
    return () => {
      active = false;
    };
  }, [setLocale]);

  useEffect(() => {
    if (caps && consumeFreshLogin()) {
      void router.navigate({ to: "/" });
    }
  }, [caps]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-density-pad text-text">
        <p role="alert" className="text-health-fault">
          {error}
        </p>
      </div>
    );
  }

  if (!caps) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text-muted" aria-busy>
        <p>Loading your session…</p>
      </div>
    );
  }

  return (
    <CapabilitiesProvider value={caps}>
      <RouterProvider router={router} />
    </CapabilitiesProvider>
  );
}
